import { useTranslation } from 'react-i18next';
import { useRef, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Box, Typography, FormControl, Select, MenuItem } from '@mui/material';

import BaseButton from '@/components/ui/BaseButton';
import BaseDialog from '@/components/ui/BaseDialog';
import { PlanogramProductInfo } from '@/types/pages/storeManagement/shelfPlanogram';

type PlanogramDialogProps = {
    open: boolean;
    planogramInfo: PlanogramProductInfo[];
    relations: Record<string, string>;
    handleOnClick: (selections: Record<string, string>) => void;
    onClose: () => void;
    onExited?: () => void;
};

// —— 多行省略（最多兩行）——
const TITLE_CLAMP_SX = {
    width: '100%',
    minWidth: 0,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    textAlign: 'center',
} as const;

const SUBTEXT_WRAP_SX = {
    width: '100%',
    minWidth: 0,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    textAlign: 'center',
} as const;

const PlanogramDialog = ({
    open,
    onClose,
    planogramInfo,
    relations,
    handleOnClick,
    onExited,
}: PlanogramDialogProps) => {
    const { t } = useTranslation('common');
    const [selections, setSelections] = useState<Record<string, string>>(relations);
    const [activeIndex, setActiveIndex] = useState(0);
    const swiperRef = useRef<import('swiper').Swiper | null>(null);

    const maxSteps = planogramInfo.length;
    const isLast = activeIndex === maxSteps - 1;

    const goNext = () => swiperRef.current?.slideNext();
    const goPrev = () => swiperRef.current?.slidePrev();

    const handleSelectChange = (productId: string, value: string) => {
        setSelections((prev) => ({ ...prev, [productId]: value }));
    };
    return (
        <BaseDialog
            open={open}
            maxWidth="xs"
            dialogTitle=""
            cancelButtonText=""
            confirmButtonText=""
            TransitionProps={{
                onEnter: () => {
                    setSelections(relations);
                },
                onExited: () => {
                    setSelections({});
                    setActiveIndex(0);
                    if (onExited) {
                        onExited();
                    }
                },
            }}
            PaperProps={{
                sx: {
                    width: 'min(100vw - 16px, 560px)',
                    m: 1,
                },
            }}
            showTopRightClose
            onClose={onClose}
        >
            <Box
                data-testid="planogram-dialog-content"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    width: '100%',
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        '& .swiper-pagination': {
                            position: 'static',
                            mt: 1,
                            display: 'flex',
                            justifyContent: 'center',
                        },
                        '& .swiper-pagination-bullets .swiper-pagination-bullet': {
                            mx: 0.5,
                        },
                        '& .swiper': { paddingBottom: 0 },
                    }}
                >
                    <Swiper
                        onSwiper={(swiper) => (swiperRef.current = swiper)}
                        onSlideChange={(swiper) => setActiveIndex(swiper.activeIndex)}
                        style={{ width: '100%' }}
                        allowTouchMove={false}
                    >
                        {planogramInfo.map((product, index) => (
                            <SwiperSlide key={`${product.barcode}-${index}`}>
                                <Box
                                    sx={{
                                        px: 2,
                                        py: 1,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 1,
                                        minWidth: 0,
                                        width: '100%',
                                    }}
                                >
                                    <Typography variant="h4" sx={TITLE_CLAMP_SX}>
                                        {product.name}
                                    </Typography>

                                    <Typography variant="h4" sx={SUBTEXT_WRAP_SX}>
                                        {product.barcode}
                                    </Typography>

                                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                        <Box sx={{ width: 'min(100%, 280px)' }}>
                                            <Typography variant="subtitle2" sx={{ mb: 0.5, textAlign: 'center' }}>
                                                {t('Select Product Options')}
                                            </Typography>

                                            <FormControl size="small" fullWidth>
                                                <Select
                                                    data-testid={`planogram-select-${product.id}`}
                                                    value={selections[product.id] ?? ''}
                                                    displayEmpty
                                                    onChange={(_event) =>
                                                        handleSelectChange(product.id, _event.target.value)
                                                    }
                                                    MenuProps={{
                                                        PaperProps: {
                                                            sx: {
                                                                maxHeight: 320,
                                                                maxWidth: '100%',
                                                                width: 'auto',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    {product.slot?.map((o, index) => {
                                                        const label = o.label
                                                            ? o.type === 'Secondary'
                                                                ? `${o.label} ( ${t('Future Shelf')} )`
                                                                : o.label
                                                            : '';
                                                        return (
                                                            <MenuItem key={`${o.value}-${index}`} value={o.value}>
                                                                {label}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            mt: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: 1,
                                            flexWrap: 'wrap',
                                            width: '100%',
                                        }}
                                    >
                                        {activeIndex > 0 && (
                                            <BaseButton
                                                data-testid="planogram-prev-btn"
                                                variant="outlined"
                                                color="primary"
                                                size="small"
                                                sx={{
                                                    minWidth: 130,
                                                    px: 1.5,
                                                    mx: 0.5,
                                                }}
                                                onClick={goPrev}
                                            >
                                                {t('Prev')}
                                            </BaseButton>
                                        )}
                                        {!isLast ? (
                                            <BaseButton
                                                data-testid="planogram-next-btn"
                                                variant="contained"
                                                color="primary"
                                                size="small"
                                                sx={{ minWidth: 130, px: 1.75, mx: 0.5 }}
                                                onClick={goNext}
                                            >
                                                {t('Next')}
                                            </BaseButton>
                                        ) : (
                                            <BaseButton
                                                data-testid="planogram-ok-btn"
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                sx={{ minWidth: 130, px: 1.75, mx: 0.5 }}
                                                onClick={() => handleOnClick(selections)}
                                            >
                                                {t('OK')}
                                            </BaseButton>
                                        )}
                                    </Box>
                                </Box>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Box>
            </Box>
        </BaseDialog>
    );
};

export default PlanogramDialog;
